import { inject, injectable, interfaces } from 'inversify';
import moment from 'moment-timezone';
import { sortBy } from '../../../../__legacy__/helpers/collections.helper';
import { SortingDirection } from '../../../../__legacy__/models/sortingDirection.model';
import { NotificationLevel, ToastNotification } from '../../../../__legacy__/modules/notifications/models/notification.model';
import { INotificationsService } from '../../../../__legacy__/modules/notifications/services/notifications.service';
import { TimePeriod } from '../../../../design/selects/timePeriodSelector/S_TimePeriodSelector';
import { IAnnotationToolsStats } from './annotationTools/models/AnnotationToolsStats';
import { IGeneralStats } from './general/models/GeneralStats';
import { defaultSortingBy, defaultSortingDirection, IProjectStatsStore } from './ProjectStats.store';
import { GetProjectStatsRequest, IGetProjectStatsService } from './services/GetProjectStats.service';
import { IWorkPerformanceStats } from './workPerformance/models/WorkPerformance';

export interface IProjectStatsBl {
  displayStatsAsync: (projectId: string, timePeriod: TimePeriod, startDate: moment.Moment, endDate: moment.Moment) => Promise<void>;
  changeAnnotationToolsStatsSorting: (orderBy: string, orderDirection: SortingDirection) => void;
  changeGeneralStatsSorting: (orderBy: string, orderDirection: SortingDirection) => void;
  changeWorkPerformanceStatsSorting: (orderBy: string, orderDirection: SortingDirection) => void;
}

export namespace IProjectStatsBl {
  export const $: interfaces.ServiceIdentifier<IProjectStatsBl> = Symbol('PROJECT_STATS_BL_TYPE');
}

@injectable()
export class ProjectStatsBl implements IProjectStatsBl {
  constructor(
    @inject(IProjectStatsStore.$) private readonly store: IProjectStatsStore,
    @inject(IGetProjectStatsService.$) private readonly getStatsService: IGetProjectStatsService,
    @inject(INotificationsService.$) private readonly notificationService: INotificationsService
  ) {}

  displayStatsAsync = async (projectId: string, timePeriod: TimePeriod, startDate: moment.Moment, endDate: moment.Moment): Promise<void> => {
    this.store.setIsLoading(true);
    try {
      const request = new GetProjectStatsRequest(projectId, timePeriod, startDate, endDate);
      const stats = await this.getStatsService.getStatsAsync(request);
      this.sortAndSetGeneralStats(stats.generalStats);
      this.sortAndSetAnnotationToolsStats(stats.annotationToolsStats);
      this.sortAndSetWorkPerformanceStats(stats.workPerformanceStats);
    } catch {
      await this.notificationService.push(new ToastNotification(NotificationLevel.ERROR, 'getting_stats_failed'));
    }

    this.store.setIsLoading(false);
  };

  changeAnnotationToolsStatsSorting = (orderBy: string, orderDirection: SortingDirection): void => {
    this.store.setAnnotationToolsStatsSorting(orderBy, orderDirection);
    this.sortAndSetAnnotationToolsStats(this.store.annotationToolsStats);
  };

  changeGeneralStatsSorting = (orderBy: string, orderDirection: SortingDirection): void => {
    this.store.setGeneralStatsSorting(orderBy, orderDirection);
    this.sortAndSetGeneralStats(this.store.generalStats);
  };

  changeWorkPerformanceStatsSorting = (orderBy: string, orderDirection: SortingDirection): void => {
    this.store.setWorkPerformanceStatsSorting(orderBy, orderDirection);
    this.sortAndSetWorkPerformanceStats(this.store.workPerformanceStats);
  };

  private sortAndSetGeneralStats = (generalStats: IGeneralStats[]): void => {
    const sorted = sortBy(generalStats, this.store.generalStatsOrderBy, this.store.generalStatsOrderDirection, defaultSortingBy, defaultSortingDirection);
    this.store.setGeneralStats(sorted);
  };

  private sortAndSetAnnotationToolsStats = (annotationToolsStats: IAnnotationToolsStats[]): void => {
    const sorted = sortBy(
      annotationToolsStats,
      this.store.annotationToolsStatsOrderBy,
      this.store.annotationToolsStatsOrderDirection,
      defaultSortingBy,
      defaultSortingDirection
    );
    this.store.setAnnotationToolsStats(sorted);
  };

  private sortAndSetWorkPerformanceStats = (workPerformanceStats: IWorkPerformanceStats[]): void => {
    const sorted = sortBy(
      workPerformanceStats,
      this.store.workPerformanceStatsOrderBy,
      this.store.workPerformanceStatsOrderDirection,
      defaultSortingBy,
      defaultSortingDirection
    );
    this.store.setWorkPerformanceStats(sorted);
  };
}
